import { PosManagedMarketplace } from 'src/utils/constants/constants';
import { EmptyListingQuery } from 'src/utils/eventQueryUtils';
import { getListingDetailsUpdateInput } from 'src/utils/inventoryUtils';
import {
  Listing,
  ListingDetails,
  ListingDetailsBroadcastSectionUpdates,
  ListingDetailsUpdateInput,
  ListingMarketplaceSeatingOverride,
  ListingQuery,
  Marketplace,
  SectionInfo,
} from 'src/WebApiController';

/**
 * Resolves the marketplace override row for a listing being mapped to `section`.
 * Matches the listing's row text to a (non-speculative) row in the section; if
 * none matches, falls back to the section's speculative row while keeping the
 * raw row text for display.
 */
export const resolveSectionOverrideRow = (
  section: SectionInfo,
  rowText: string | null | undefined
): { overrideRow: string | null; overrideRowId: number | null } => {
  const matched = rowText
    ? section.rows.find(
        // Case-insensitive to match the backend bulk resolution
        // (BulkListingControllerService uses StringComparison.OrdinalIgnoreCase).
        (r) =>
          !r.isSpeculative && r.name?.toLowerCase() === rowText.toLowerCase()
      )
    : undefined;
  if (matched) {
    return { overrideRow: matched.name, overrideRowId: matched.id };
  }
  return {
    overrideRow: section.specRow?.name ?? rowText ?? null,
    overrideRowId: section.specRow?.id ?? null,
  };
};

/**
 * Builds a broadcast-section update that maps the listing's StubHub marketplace
 * seating override to the selected section, while preserving the overrides for
 * any other (non-managed) marketplaces the listing already has.
 *
 * Mirrors `getListingDetailsBroadcastInput` in
 * `ListingDetails/ListingForms/BroadcastForm/BroadcastForm.hooks.ts` so that a
 * single-listing map behaves the same as the listing details page.
 */
export const buildSectionMappingInput = (
  listing: Listing,
  section: SectionInfo
): ListingDetailsBroadcastSectionUpdates => {
  const existingOverrides: ListingMarketplaceSeatingOverride[] =
    listing.mkpListings
      ?.filter((m) => m.mkp && !PosManagedMarketplace.includes(m.mkp))
      ?.map((m) => ({
        marketplace: m.mkp,
        overrideSection: m.overrideSection,
        overrideRow: m.overrideRow,
        overrideSeats: m.overrideSeats,
        overrideRowId: m.overrideRowId,
        overrideSectionId: m.overrideSectionId,
      })) ?? [];

  const stubHubIndex = existingOverrides.findIndex(
    (o) => o.marketplace === Marketplace.StubHub
  );
  const existingStubHub =
    stubHubIndex >= 0 ? existingOverrides[stubHubIndex] : undefined;

  // Map the row within the new section: match by the listing's row text, else
  // fall back to the section's speculative row.
  const { overrideRow, overrideRowId } = resolveSectionOverrideRow(
    section,
    listing.seating.row
  );

  const stubHubOverride: ListingMarketplaceSeatingOverride = {
    marketplace: Marketplace.StubHub,
    overrideSection: section.name,
    overrideSectionId: section.id,
    overrideRow,
    overrideRowId,
    overrideSeats: existingStubHub?.overrideSeats ?? null,
  };

  const marketplaceSeatOverrides =
    stubHubIndex >= 0
      ? existingOverrides.map((o, i) =>
          i === stubHubIndex ? stubHubOverride : o
        )
      : [...existingOverrides, stubHubOverride];

  return {
    id: listing.id,
    rowVersion: null,
    adminHoldNotes: listing.adminHoldNotes ?? null,
    enableNativeSync: listing.enableNativeSync ?? null,
    marketplaceSeatOverrides,
  };
};

/**
 * A listing query scoped to the given events and an exact (raw) section name —
 * i.e. every listing with the same section name across events sharing the venue
 * config. Feeds the bulk marketplace-seating-override preview/apply.
 */
export const buildSameSectionBulkQuery = (
  eventVirtIds: string[],
  section: string
): ListingQuery => ({
  ...EmptyListingQuery,
  eventOrMappingIds: eventVirtIds,
  sectionEquals: section,
});

/**
 * Builds the bulk update input carrying only the StubHub section override.
 * Mirrors BulkEditMarketplaceSeatingDialog: the bulk endpoint reads only
 * `marketplaceSeatOverrides` from this input.
 *
 * No row is set here — the caller passes `overrideRowIfNotPassed`, and the bulk
 * endpoint resolves each listing's row against the target section (row-name
 * match, else the section's speculative row) using the venue config, per-listing.
 * `overrideSectionId` is included so the backend can match the target section.
 */
export const buildBulkSectionOverrideInput = (
  section: SectionInfo
): ListingDetailsUpdateInput =>
  getListingDetailsUpdateInput({
    mkpListings: [
      {
        mkp: Marketplace.StubHub,
        overrideSection: section.name,
        overrideSectionId: section.id,
        overrideRow: null,
        overrideSeats: null,
        overrideRowId: null,
      },
    ],
  } as unknown as ListingDetails);
